/*
 * The deck browser's full-page background wipe.
 *
 * A disc grows from a clicked control to cover the viewport, then becomes the new
 * page tint (`--page-tint`, read by AppShell and the modal screen). Shared by the
 * card-view family switch and the card/grid view toggle so both animate the same
 * way. The old background is held until the disc lands, so nothing snaps.
 *
 * The tint is set imperatively (here), never from a reactive effect — a reactive
 * set would fire on the state change and beat the wipe to the new colour.
 */

const RM =
  typeof matchMedia !== "undefined" && matchMedia("(prefers-reduced-motion: reduce)").matches

/** A family's page tint — a soft wash of its deck glow over the sand. */
export const tintFor = (n: number) =>
  `color-mix(in srgb, var(--family-${n}-deck-glow) 14%, var(--color-surface-page))`

/** Pass as the tint to wipe back to the neutral page wash (grid view). */
export const NEUTRAL_TINT = ""

export function setPageTint(tint: string) {
  const root = document.documentElement.style
  if (tint) root.setProperty("--page-tint", tint)
  else root.removeProperty("--page-tint")
}

/**
 * Wipe the page background to `tint` ("" = neutral wash), anchored at the centre
 * of `origin`. Applies the tint when the disc lands. Under reduced motion it sets
 * the tint immediately with no disc.
 */
export function wipePageTint(origin: HTMLElement, tint: string) {
  if (RM) {
    setPageTint(tint)
    return
  }
  const r = origin.getBoundingClientRect()
  const x = r.left + r.width / 2
  const y = r.top + r.height / 2
  const radius = Math.hypot(Math.max(x, innerWidth - x), Math.max(y, innerHeight - y))
  const circle = document.createElement("div")
  circle.style.cssText = [
    "position:fixed",
    "z-index:0",
    "border-radius:50%",
    "pointer-events:none",
    "will-change:transform",
    `width:${2 * radius}px`,
    `height:${2 * radius}px`,
    `left:${x - radius}px`,
    `top:${y - radius}px`,
    `background:${tint || "var(--color-surface-page-image)"}`,
  ].join(";")
  document.body.appendChild(circle)
  const animation = circle.animate([{ transform: "scale(0)" }, { transform: "scale(1)" }], {
    duration: 650,
    easing: "cubic-bezier(.65,0,.35,1)",
  })
  animation.onfinish = () => {
    setPageTint(tint)
    circle.remove()
  }
}
